import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { countryCodes } from "app/utils/CountryCodes";
import { cls } from "app/utils";
// Same look as the shared CountrySelector: the wizard's country field only
// differs in its keyboard and assistive-tech behavior, and in how the open
// list is positioned (ownStyles).
import styles from "app/components/inputs/CountrySelector/styles.module.scss";
import ownStyles from "./styles.module.scss";

interface IProps {
  id: string;
  selected?: string;
  onSelect: (code: string) => void;
  placeholder?: string;
  /** Announced to assistive tech; the form is noValidate, so validation stays the step's. */
  required?: boolean;
  /** Left padding of the input, which the flag sits inside. */
  left?: number;
}

// Space between the input and the floating list — the shared list's margin.
const DROPDOWN_GAP = 5;

// The shared CountrySelector renders a plain text input over an unannotated
// list, so a screen reader hears a free-text field and a keyboard user cannot
// tell the list is there. This wizard-only copy follows the WAI-ARIA editable
// combobox pattern: the input owns the listbox, arrows move
// aria-activedescendant, Enter picks only while the list is open, and a
// closed field lets Enter reach the step's submit. Its list is position:
// fixed and anchored to the input by measurement, flipping above it when the
// viewport runs out (see styles.module.scss). Other pages keep the shared
// component untouched.
const WizardCountryField = ({
  id,
  selected,
  onSelect,
  placeholder = "Select Country",
  required = false,
  left = 16,
}: IProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const listboxId = `${id}-listbox`;
  const optionId = (index: number) => `${id}-option-${index}`;
  const selectedCountry =
    countryCodes.find((country) => country.code === selected) || null;
  const filtered = countryCodes.filter((country) =>
    country.name.toLowerCase().includes(search.toLowerCase()),
  );
  const activeIndex = Math.min(highlighted, filtered.length - 1);

  const open = () => {
    setSearch("");
    // Start on the current choice so Enter keeps it.
    setHighlighted(
      Math.max(
        0,
        countryCodes.findIndex((country) => country.code === selected),
      ),
    );
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setSearch("");
  };

  const pick = (country: { code: string }) => {
    onSelect(country.code);
    close();
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => {
      document.removeEventListener("mousedown", onOutside);
    };
  }, [isOpen]);

  // Anchors the fixed list to the input. Runs after render (the list must
  // exist to measure its height for the flip decision) and again whenever
  // the filter changes that height, then on any scroll or resize — a fixed
  // box does not follow ancestor scrolling, so re-anchoring keeps it glued to
  // the input while the wizard card scrolls under it.
  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const anchor = () => {
      const field = inputRef.current;
      const list = dropdownRef.current;
      if (!field || !list) {
        return;
      }
      const rect = field.getBoundingClientRect();
      const openUp =
        rect.bottom + DROPDOWN_GAP + list.offsetHeight > window.innerHeight;

      list.style.width = `${rect.width}px`;
      list.style.left = `${rect.left}px`;
      list.style.top = openUp
        ? `${rect.top - DROPDOWN_GAP - list.offsetHeight}px`
        : `${rect.bottom + DROPDOWN_GAP}px`;
    };

    anchor();
    // Capture phase, since scroll does not bubble from the card.
    window.addEventListener("scroll", anchor, true);
    window.addEventListener("resize", anchor);
    return () => {
      window.removeEventListener("scroll", anchor, true);
      window.removeEventListener("resize", anchor);
    };
  }, [isOpen, filtered.length]);

  // Keep the active option in view while arrows move it.
  useEffect(() => {
    if (isOpen && activeIndex >= 0) {
      listRef.current
        ?.querySelector(`#${optionId(activeIndex)}`)
        ?.scrollIntoView({ block: "nearest" });
    }
  }, [isOpen, activeIndex]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (["ArrowDown", "ArrowUp"].includes(event.key)) {
        event.preventDefault();
        open();
        return;
      }
      // A closed field shows the selected country's name as its value, so a
      // typed character would land on the end of that name and match
      // nothing. Typing starts a fresh search instead, as the mouse path
      // does (a click clears it); Backspace and Delete open an empty one.
      const printable =
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey;
      if (printable || event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        setSearch(printable ? event.key : "");
        setHighlighted(0);
        setIsOpen(true);
      }
      // Enter on a closed field is the form's, not the list's.
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlighted(Math.min(activeIndex + 1, filtered.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlighted(Math.max(activeIndex - 1, 0));
        break;
      case "Home":
        event.preventDefault();
        setHighlighted(0);
        break;
      case "End":
        event.preventDefault();
        setHighlighted(filtered.length - 1);
        break;
      case "Enter":
        event.preventDefault();
        if (filtered[activeIndex]) {
          pick(filtered[activeIndex]);
        }
        break;
      case "Escape":
        event.preventDefault();
        close();
        break;
      case "Tab":
        close();
        break;
      default:
    }
  };

  const showFlag = !isOpen && !!selectedCountry;

  return (
    <div className={styles.country_selector_container} ref={containerRef}>
      <div className={styles.input_wrapper}>
        {showFlag && (
          <span className={styles.selected_flag} style={{ left: `${left}px` }}>
            {selectedCountry.flag}
          </span>
        )}
        <input
          id={id}
          ref={inputRef}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          aria-activedescendant={
            isOpen && filtered[activeIndex] ? optionId(activeIndex) : undefined
          }
          aria-required={required || undefined}
          value={isOpen ? search : selectedCountry ? selectedCountry.name : ""}
          placeholder={placeholder}
          onChange={(event) => {
            setSearch(event.target.value);
            setHighlighted(0);
            if (!isOpen) {
              setIsOpen(true);
            }
          }}
          onClick={() => {
            if (!isOpen) {
              open();
            }
          }}
          onKeyDown={onKeyDown}
          className={cls(styles.country_input, { [styles.has_flag]: showFlag })}
          style={{ paddingLeft: showFlag ? `${left + 25}px` : `${left}px` }}
        />
      </div>
      {isOpen && (
        <div className={ownStyles.dropdown} ref={dropdownRef}>
          {filtered.length > 0 ? (
            <ul
              id={listboxId}
              role="listbox"
              aria-label={placeholder}
              className={styles.country_list}
              ref={listRef}
            >
              {filtered.map((country, index) => (
                <li
                  key={country.code}
                  id={optionId(index)}
                  role="option"
                  aria-selected={country.code === selected}
                  onClick={() => pick(country)}
                  onMouseEnter={() => setHighlighted(index)}
                  className={cls(styles.country_item, {
                    [styles.highlighted]: index === activeIndex,
                    [styles.selected]: country.code === selected,
                  })}
                >
                  <span className={styles.country_flag} aria-hidden="true">
                    {country.flag}
                  </span>
                  <span className={styles.country_name}>{country.name}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.no_results} role="status">
              {t("No countries found")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WizardCountryField;
